// src/pages/auth/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Lock, ArrowLeft, KeyRound, Mail } from 'lucide-react';

interface LoginPageProps {
  onShowSignup?: () => void;
  onLoginSuccess?: () => void;
  initialUserType?: 'buyer' | 'seller';
}

const LoginPage: React.FC<LoginPageProps> = ({ onShowSignup, onLoginSuccess, initialUserType = 'buyer' }) => {
  // 1. User context se 'user' object bhi nikalein taaki check kar sakein wo logged in hai ya nahi
  const { user, login } = useUser(); 
  const navigate = useNavigate();
  const location = useLocation();

  // --- States ---
  const [view, setView] = useState<'login' | 'forgot' | 'reset'>('login');
  const [userType, setUserType] = useState<'buyer' | 'seller'>(initialUserType);
  const [formData, setFormData] = useState({ email: '', password: '' });
  
  // Forgot Password States
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => { setUserType(initialUserType); }, [initialUserType]);

  // --- FIX 1: AUTO-REDIRECT IF ALREADY LOGGED IN ---
  // Agar user pehle se login hai aur galti se back button daba de, toh wapas dashboard bhej do.
  useEffect(() => {
    if (user) {
      // Check karein user ka role kya hai (apne context ke hisab se adjust karein)
      // Assuming user object has 'role' or you can deduce based on logic
      // Yahan main safe play kar raha hu:
      const targetPath = userType === 'seller' ? '/seller/dashboard' : '/buyer/profile'; 
      // Agar user object me role hai to: user.role === 'seller' ? ... : ...
      
      navigate(targetPath, { replace: true });
    }
  }, [user, navigate, userType]);


  // --- HANDLERS ---
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setMessage(null);
    try {
      const endpoint = userType === 'buyer' ? 'buyers' : 'sellers';
      const response = await fetch(`/springboot/api/${endpoint}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password, userType }),
      });
      const data = await response.json();
      if (response.ok) {
        login(data);
        if (onLoginSuccess) onLoginSuccess();
        
        // --- FIX 2: REPLACE HISTORY ---
        // 'replace: true' ka matlab hai Login page ko history se mita do.
        // Ab Back button dabane par Login page nahi aayega.
        navigate(userType === 'buyer' ? '/buyer/profile' : '/seller/dashboard', { replace: true });
        
      } else { setMessage({ type: 'error', text: data.message || 'Login failed.' }); }
    } catch (err) { setMessage({ type: 'error', text: 'Server error. Try again.' }); } 
    finally { setLoading(false); }
  };

  // ... (Baaki saare Handlers - handleSendOtp, handleResetPassword SAME rahenge) ...
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setMessage(null);
    try {
      const response = await fetch('/springboot/api/auth/forgot-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: resetIdentifier, userType }),
      });
      const data = await response.json();
      if (response.ok) {
        setMaskedEmail(data.maskedEmail);
        setMessage({ type: 'success', text: `OTP sent to ${data.maskedEmail}` });
        setView('reset');
      } else { setMessage({ type: 'error', text: typeof data === 'string' ? data : 'User not found.' }); }
    } catch (err) { setMessage({ type: 'error', text: 'Failed to send OTP.' }); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setMessage(null);
    try {
      const response = await fetch('/springboot/api/auth/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: resetIdentifier, otp, newPassword, userType }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Password reset successfully! Please login.' });
        setTimeout(() => setView('login'), 2000);
      } else { const text = await response.text(); setMessage({ type: 'error', text: text }); }
    } catch (err) { setMessage({ type: 'error', text: 'Reset failed. Try again.' }); }
    finally { setLoading(false); }
  };


  // --- STYLING CONSTANTS ---
  const inputClass = "w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-gray-700 font-medium text-sm shadow-sm hover:bg-white";
  const iconClass = "absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400 pointer-events-none transition-colors group-focus-within:text-emerald-500";

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 animate-fade-in">
      <div className="w-full max-w-sm">
        
        {/* HEADER */}
        <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight">
            {view === 'login' ? 'Welcome Back' : 'Recovery'}
            </h2>
            {view === 'login' && (
                <p className="text-gray-500 text-sm mt-2 font-medium">
                    Please login to your <span className="text-emerald-600 font-bold capitalize">{userType}</span> account
                </p>
            )}
        </div>
        
        {/* MESSAGE ALERT */}
        {message && (
          <div className={`p-3 mb-6 rounded-xl text-center text-sm font-semibold shadow-sm ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* --- VIEW 1: LOGIN FORM --- */}
        {view === 'login' && (
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="relative group">
              <span className={iconClass}><User size={20} /></span>
              <input
                type="text"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className={inputClass}
                placeholder="Email or Mobile Number"
                required
              />
            </div>
            <div className="relative group">
              <span className={iconClass}><Lock size={20} /></span>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className={inputClass}
                placeholder="Password"
                required
              />
            </div>
            
            <div className="flex justify-end">
                <button
                type="button"
                onClick={() => { setView('forgot'); setMessage(null); }}
                className="text-sm text-emerald-600 hover:text-emerald-800 font-bold transition-colors"
                >
                Forgot Password?
                </button>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 hover:shadow-xl transform transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed">
              {loading ? 'Authenticating...' : 'Secure Login'}
            </button>
          </form>
        )}

        {/* --- VIEW 2: FORGOT PASSWORD --- */}
        {view === 'forgot' && (
          <form onSubmit={handleSendOtp} className="space-y-5">
             <div className="bg-blue-50 p-4 rounded-xl text-center border border-blue-100 mb-4">
                <p className="text-blue-800 text-xs font-semibold">
                    We will send an OTP to your registered Email address.
                </p>
             </div>
             <div className="relative group">
               <span className={iconClass}><Mail size={20} /></span>
               <input
                 type="text"
                 value={resetIdentifier}
                 onChange={(e) => setResetIdentifier(e.target.value)}
                 className={inputClass}
                 placeholder="Enter Registered Email"
                 required
               />
             </div>
             <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-md hover:bg-emerald-700 transition-all">
               {loading ? 'Sending OTP...' : 'Get Verification Code'}
             </button>
             <button type="button" onClick={() => setView('login')} className="w-full text-gray-500 text-sm font-semibold flex items-center justify-center hover:text-gray-800 transition-colors mt-2">
               <ArrowLeft size={16} className="mr-2"/> Back to Login
             </button>
          </form>
        )}

        {/* --- VIEW 3: RESET PASSWORD --- */}
        {view === 'reset' && (
          <form onSubmit={handleResetPassword} className="space-y-5">
             <p className="text-center text-emerald-700 text-xs font-bold bg-emerald-50 p-2 rounded-lg border border-emerald-100">
               OTP sent to: {maskedEmail}
             </p>
             <div className="relative group">
               <span className={iconClass}><KeyRound size={20} /></span>
               <input
                 type="text"
                 value={otp}
                 onChange={(e) => setOtp(e.target.value)}
                 className={inputClass}
                 placeholder="Enter OTP"
                 required
               />
             </div>
             <div className="relative group">
               <span className={iconClass}><Lock size={20} /></span>
               <input
                 type="password"
                 value={newPassword}
                 onChange={(e) => setNewPassword(e.target.value)}
                 className={inputClass}
                 placeholder="New Strong Password"
                 required
               />
             </div>
             <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-md hover:bg-emerald-700 transition-all">
               {loading ? 'Resetting...' : 'Update Password'}
             </button>
             <button type="button" onClick={() => setView('login')} className="w-full text-gray-500 text-sm font-semibold flex items-center justify-center hover:text-gray-800 mt-2">
               Cancel
             </button>
          </form>
        )}

        {/* SIGNUP LINK */}
        {view === 'login' && onShowSignup && (
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-500 text-sm font-medium">
              Don't have an account?{' '}
              <button className="text-emerald-600 hover:text-emerald-800 font-bold hover:underline transition-all ml-1" onClick={(e) => { e.preventDefault(); onShowSignup(); }}>
                Create Account
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;